import type { BlogTileData } from '@bulknutrients/bulk-shared-lib/interfaces/Blog';
import { inputTypes } from '@bulknutrients/bulk-shared-lib/interfaces/IForms';
import type { CloudinaryAsset } from '@bulknutrients/bulk-shared-lib/interfaces/Sanity';
import { getFeaturedFaqs } from '@bulknutrients/bulk-shared-lib/lib/sanity/faq';
import { Accordion } from '@components/Accordion';
import { BulkErrorBoundary } from '@components/Alerts/ErrorBoundaryFallback';
import { BlogCard } from '@components/Blog/BlogCard';
import { Button } from '@components/Button';
import { EmblaCarousel } from '@components/Carousels/Carousel';
import { Container } from '@components/Container';
import { Input } from '@components/FormComponents';
import { CustomImage } from '@components/Media/Image';
import {
  type Message,
  messages,
} from '@components/NewsletterSubForm/NewsletterSubForm';
import { PortableTextBlockRenderer } from '@components/PortableText/PortableText';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import {
  RewardsLink,
  TextLink,
} from '@components/Typography/TextLink/TextLink';
import { featuredBlogs } from '@data/featuredBlogs';
import { useKlaviyo } from '@hooks/useKlaviyo';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { IoMdCheckmarkCircle } from 'react-icons/io';

function SocialIcon({ children }: { children: React.ReactNode }) {
  return (
    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-black bg-center bg-no-repeat">
      {children}
    </span>
  );
}

export function Footer() {
  const [faqs, setFaqs] = useState<{ title: string; content: any }[]>([]);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [msgToDisplay, setMsgToDisplay] = useState<Message>(messages.SUCCESS);
  const [platform, setPlatform] = useState('web');
  const [isDarkMode, setIsDarkMode] = useState(false);

  const { newsletterSubscribe } = useKlaviyo();

  const rhfMethods = useForm({
    mode: 'onTouched',
    defaultValues: {
      email: '',
    },
  });

  const handleSubscription = async ({ email }) => {
    const success = await newsletterSubscribe({
      email,
      source: 'footer',
    });

    setSubmitted(true);
    setMsgToDisplay(success ? messages.SUCCESS : messages.ERROR);
  };

  useEffect(() => {
    // get platform to display app store links on mobile
    const userAgent = navigator.userAgent;
    if (/iPad|iPhone|iPod/.test(userAgent)) {
      setPlatform('ios');
    } else if (/android/i.test(userAgent)) {
      setPlatform('android');
    } else {
      setPlatform('web');
    }
  }, []);

  useEffect(() => {
    (async () => {
      const featuredFaqs = await getFeaturedFaqs();
      const formattedFeaturedFaqs = featuredFaqs.map((f) => {
        return {
          title: f.title,
          content: <PortableTextBlockRenderer content={f.body} />,
        };
      });
      setFaqs(formattedFeaturedFaqs);
    })();
  }, []);

  // Detect dark mode
  useEffect(() => {
    const prefersDarkScheme = window.matchMedia('(prefers-color-scheme: dark)');
    if (prefersDarkScheme.matches) {
      // User prefers a dark color scheme
      setIsDarkMode(true);
    } else {
      // User prefers a light color scheme or has not specified a preference
      setIsDarkMode(false);
    }
  }, []);

  return (
    <Container as="footer" className="lg:mt-8">
      <BulkErrorBoundary location="Footer">
        <div className="bg-black dark:bg-grey-darkest py-8 my-6 full-bleed text-white flex justify-center lg:mt-15">
          <div className="lg:max-h-[200px] max-w-max-width grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 items-center">
            {/* GET THE APP */}
            <div className="lg:max-h-[200px] flex flex-row flex-1 justify-center sm:justify-end items-center lg:col-span-2 sm:px-6 pb-6">
              <div className="hidden lg:block">
                <CustomImage
                  image={
                    {
                      asset: {
                        public_id: 'phones_1_upqhho',
                        _type: 'cloudinary.asset',
                      },
                      ratio: 1.1,
                      altText: 'Bulk Nutrients app displayed on mobile phone',
                      internalLink:
                        '/faqs/customer-support-accounts/do-you-have-an-app',
                    } as CloudinaryAsset
                  }
                  width={385}
                  templateOverride={{ crop: 'auto' }}
                />
              </div>
              <div>
                <div className="uppercase font-extrabold text-2xl mb-2">
                  <TextLink
                    href="/faqs/customer-support-accounts/do-you-have-an-app"
                    className="text-orange no-underline"
                  >
                    Get our app!
                  </TextLink>
                </div>
                <div className="font-bold flex flex-row gap-2">
                  <IoMdCheckmarkCircle size={20} color="hsl(33, 80%, 52%)" />
                  Have easier access to all our products
                </div>
                <div className="flex flex-row gap-2">
                  <IoMdCheckmarkCircle size={20} color="hsl(33, 80%, 52%)" />
                  Easy convenient shopping experience
                </div>
                <div className="flex flex-row gap-2">
                  <IoMdCheckmarkCircle size={20} color="hsl(33, 80%, 52%)" />
                  App exclusive discounts and much more
                </div>
                <div className="flex flex-row gap-2">
                  <IoMdCheckmarkCircle size={20} color="hsl(33, 80%, 52%)" />
                  Save products for quick and easy checkout
                </div>
                {platform !== 'web' && (
                  <div className="flex flex-row gap-6 lg:hidden justify-center mt-4">
                    {/* TODO only show relevant store, not necessary to show both if ios/android */}
                    <a href="https://apps.apple.com/app/6744590394">
                      <CustomImage
                        image={
                          {
                            asset: {
                              public_id: isDarkMode
                                ? 'fqgeuou5kuko0ll6kyti'
                                : 'utofva7z0smytzrphiuv',
                              _type: 'cloudinary.asset',
                            },
                            ratio: 3.4,
                            altText: 'Download on the App Store',
                          } as CloudinaryAsset
                        }
                        width={160}
                        templateOverride={{
                          crop: 'auto',
                          width: 160,
                        }}
                      />
                    </a>
                    <a href="https://play.google.com/store/apps/details?id=com.bulknutrients.app">
                      <CustomImage
                        image={
                          {
                            asset: {
                              public_id: isDarkMode
                                ? 'kazbbavdfjcf0yy0far2'
                                : 'c71yf9os4j6tzcpvmfoe',
                              _type: 'cloudinary.asset',
                            },
                            ratio: 3.4,
                            altText: 'Get it on Google Play',
                          } as CloudinaryAsset
                        }
                        width={160}
                        templateOverride={{
                          crop: 'auto',
                          width: 160,
                        }}
                      />
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* NEWSLETTER SUBSCRIPTION FORM */}
            <div className="flex flex-col flex-1 border-t sm:border-t-0 sm:border-l border-grey h-full gap-4 justify-center px-2 tiny:px-6 pt-6">
              <div className="font-bold">Subscribe to our email</div>

              {!submitted ? (
                <>
                  <div>
                    And be the first to know about exclusive promotions and
                    releases.
                  </div>
                  <FormProvider {...rhfMethods}>
                    <form
                      onSubmit={rhfMethods.handleSubmit(handleSubscription)}
                      className="flex flex-row"
                    >
                      <div className="flex-1">
                        <Input
                          type={inputTypes.email}
                          name="email"
                          label=""
                          placeholder="Email"
                          required
                          className="!rounded-r-none min-w-[150px]"
                        />
                      </div>
                      <Button
                        type="submit"
                        buttonStyle="primary"
                        className={`h-10 rounded-l-none flex-shrink-0 ${
                          rhfMethods.formState.errors?.email ? 'mt-5' : 'mt-1'
                        }`}
                      >
                        Subscribe
                      </Button>
                    </form>
                  </FormProvider>
                </>
              ) : (
                // large padding to prevent page shifting to a smaller component
                <div className="text-center">
                  <h2 className="font-bold text-orange">
                    {msgToDisplay.header}
                  </h2>
                  <Paragraph>{msgToDisplay.msg}</Paragraph>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* POPULAR BLOGS CAROUSEL */}
        <div className="full-bleed py-6 pl-6">
          <EmblaCarousel
            title="Popular content"
            titleClass="!font-extrabold"
            slides={featuredBlogs.map((b, idx) => {
              return (
                <BlogCard
                  blog={b as unknown as BlogTileData}
                  key={`featuredBlog${idx}`}
                  className="w-xs sm:w-[400px]"
                />
              );
            })}
          />
        </div>

        <div className="grid grid-cols-1 max-w-max-width my-4 gap-8 lg:grid-cols-[2fr_1fr_1fr]">
          {/* FAQS */}
          <div className="flex gap-4 flex-col">
            <Button
              buttonStyle="text"
              href="/faqs"
              className="font-extrabold text-2xl w-full text-center lg:text-left"
            >
              Frequently Asked Questions
            </Button>
            <div>
              <Accordion defaultOpen={false} variant="pill" items={faqs} />
            </div>
          </div>

          {/* CONTACT */}
          <div className="flex gap-4 flex-col order-last lg:order-0">
            <Button
              buttonStyle="text"
              href="/contact-us"
              className="font-extrabold text-2xl w-full text-center lg:text-left"
            >
              Contact
            </Button>
            <Paragraph className="my-0 text-center lg:text-left">
              Have questions or need a hand? Our friendly customer service team
              is here to help with everything from product advice to order
              changes.
            </Paragraph>
            <Paragraph className="text-center lg:text-left my-0">
              <span>
                <strong>Phone:</strong>{' '}
                <TextLink href="tel:+61362664725">03 6266 4725</TextLink>
              </span>
              <br />
              <span>
                <strong>Email:</strong>{' '}
                <TextLink href="mailto:info@bulknutrients.com.au">
                  info@bulknutrients.com.au
                </TextLink>
              </span>
              <br />
              <span>
                <strong>Live Chat:</strong> Look for the button in the bottom
                right-hand corner of your screen
              </span>
              <br />
            </Paragraph>
            <Paragraph className="text-center lg:text-left my-0">
              Or fill in our{' '}
              <TextLink href="/contact-us">online contact form</TextLink> and
              we'll get back to you soon!
            </Paragraph>
          </div>

          {/* ABOUT US */}
          <div className="flex gap-4 flex-col">
            <Button
              buttonStyle="text"
              href="/about-us"
              className="font-extrabold text-2xl w-full text-center lg:text-left"
            >
              Info
            </Button>
            <ul className="grid-cols-2 grid lg:grid-cols-1 text-center lg:text-left">
              <li>
                <TextLink href="/about-us">About Bulk Nutrients</TextLink>
              </li>
              <li>
                <TextLink href="/categories/shop-by-recommended">
                  Ambassadors
                </TextLink>
              </li>
              <li>
                <TextLink href="/careers">Careers @ Bulk</TextLink>
              </li>
              <li>
                <TextLink href="/community-support">Community Support</TextLink>
              </li>
              <li>
                <TextLink href="/contact-us">Contact Us</TextLink>
              </li>
              <li>
                <TextLink href="/faqs">FAQs</TextLink>
              </li>
              <li>
                <TextLink href="/giftcertificates">Gift Certificates</TextLink>
              </li>
              <li>
                <TextLink href="/faqs/ingredients-sourcing-product-information/bulk-nutrients-hasta-lab-test-results">
                  HASTA Certification
                </TextLink>
              </li>
              <li>
                <TextLink href="/news">Latest News</TextLink>
              </li>
              <li>
                <TextLink href="/partners">Partners</TextLink>
              </li>
              <li>
                <TextLink href="/faqs/ingredients-sourcing-product-information/bulk-nutrients-protein-lab-test-results">
                  Protein Testing
                </TextLink>
              </li>
              <li>
                <RewardsLink>Rewards</RewardsLink>
              </li>
              <li>
                <TextLink href="/bulk-nutrients-social-hub">
                  Social Hub
                </TextLink>
              </li>
              <li>
                <TextLink href="/faqs/shipping">
                  Ordering &amp; Shipping
                </TextLink>
              </li>
              <li>
                <TextLink href="/contact-us/returns">Returns</TextLink>
              </li>
            </ul>
          </div>
        </div>

        {/* ACKNOWLEDGEMENT */}
        <Paragraph className="text-center font-bold">
          We acknowledge the Traditional Owners of the land on which our
          organisation operates, the Melukerdee People of the South East Nation
          and pay our respects to Elders past, present and emerging.
        </Paragraph>

        <div className="full-bleed bg-orange text-white pb-12 lg:pb-0">
          {/* SOCIALS */}
          <div className="mx-auto max-w-max-width items-center p-6 flex flex-col gap-8">
            <div
              data-testid="footer-variant-socials"
              className="mx-0 my-[1em] flex gap-4"
            >
              <a
                href="https://www.facebook.com/bulknutrients"
                target="_blank"
                rel="noopener noreferrer"
              >
                <SocialIcon>
                  <Image
                    src="/images/facebook.svg"
                    width={12}
                    height={22}
                    alt="Bulk Nutrients on Facebook"
                  />
                </SocialIcon>
              </a>
              <a
                href="https://www.instagram.com/bulknutrients/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <SocialIcon>
                  <Image
                    src="/images/instagram.svg"
                    width={19}
                    height={19}
                    alt="Check out Bulk Nutrients' Instagram posts"
                  />
                </SocialIcon>
              </a>
              <a
                href="https://www.tiktok.com/@bulk.nutrients"
                target="_blank"
                rel="noopener noreferrer"
              >
                <SocialIcon>
                  <Image
                    src="/images/tiktok.svg"
                    width={38}
                    height={38}
                    alt="View Bulk Nutrients' TikTok content"
                  />
                </SocialIcon>
              </a>
              <a
                href="https://www.youtube.com/user/BulkNutrients/feed"
                target="_blank"
                rel="noopener noreferrer"
              >
                <SocialIcon>
                  <Image
                    src="/images/youtube.svg"
                    width={20}
                    height={24}
                    alt="View Bulk Nutrients' YouTube Channel"
                  />
                </SocialIcon>
              </a>
            </div>

            {/* FOOTER CATEGORY BAR */}
            <div className="flex justify-center items-center flex-col md:flex-row md:flex-wrap gap-2">
              <TextLink
                href="/categories/top-sellers"
                className="uppercase text-white no-underline tracking-[.25rem] md:border-r-2 border-black px-3"
              >
                Top Products
              </TextLink>
              <TextLink
                href="/categories/shop-by-ingredient"
                className="uppercase text-white no-underline tracking-[.25rem] md:border-r-2 border-black px-3"
              >
                Shop by ingredient
              </TextLink>
              <TextLink
                href="/blog"
                className="uppercase text-white no-underline tracking-[.25rem] md:border-r-2 border-black px-3"
              >
                Bulk Blog
              </TextLink>
              <TextLink
                href="/blog/recipes"
                className="uppercase text-white no-underline tracking-[.25rem] md:border-r-2 border-black px-3"
              >
                Recipes
              </TextLink>
              <TextLink
                href="/contact-us"
                className="uppercase text-white no-underline tracking-[.25rem] px-3"
              >
                Contact
              </TextLink>
            </div>
          </div>
          <div className="text-sm flex flex-col justify-center items-center gap-2 my-6 px-2">
            <p className="flex flex-wrap text-center">
              All prices are in Australian dollars (AUD) and include GST unless
              otherwise stated.
              <br />
              All content copyright &copy; Bulk Nutrients 2008 - 2026
            </p>
            <div className="flex flex-wrap justify-center items-center text-white">
              <span className="mx-2 font-bold sm:hidden">•</span>
              <TextLink
                href="/bulk-nutrients-terms-conditions"
                className="no-underline font-normal text-white"
              >
                Terms & Conditions
              </TextLink>
              <TextLink
                href="/bulk-nutrients-packaging-sustainability-strategy"
                className="no-underline font-normal text-white"
              >
                <span className="mx-2 font-bold !text-white">•</span>
                Sustainability Strategy
              </TextLink>
              <TextLink
                href="/bulk-nutrients-privacy-policy"
                className="no-underline font-normal text-white"
              >
                <span className="mx-2 font-bold !text-white">•</span>
                Privacy Policy
              </TextLink>

              <TextLink
                href="/payments"
                className="no-underline font-normal text-white"
              >
                <span className="mx-2 font-bold !text-white">•</span>
                Payment Information
              </TextLink>
              <TextLink
                href="/sitemap"
                className="no-underline font-normal text-white"
              >
                <span className="mx-2 font-bold !text-white">•</span>
                Sitemap
              </TextLink>
            </div>
          </div>
        </div>
      </BulkErrorBoundary>
    </Container>
  );
}
